import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import api from "../../api"; // axios instance with token + school_id interceptor

const ProductSubcategory = () => {
  const [form, setForm] = useState({ category_id: "", subcategory_name: "" });
  const [categories, setCategories] = useState([]); // parent categories
  const [subcategories, setSubcategories] = useState([]);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState(null);

  const created_by = localStorage.getItem("id");
  const schoolId = localStorage.getItem("school_id");

  const categoryURL = `/product/category/list`;
  const baseURL = `/product/subcategory`;

  // ================= FETCH CATEGORIES =================
  const fetchCategories = async () => {
    try {
      const res = await api.get(categoryURL, {
        headers: { "X-School-Id": schoolId },
      });
      const list = res.data;
      setCategories(Array.isArray(list) ? list : list?.data ?? []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  // ================= FETCH SUBCATEGORIES =================
  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) return setSubcategories([]);
    try {
      const res = await api.get(`${baseURL}/list?category_id=${categoryId}`, {
        headers: { "X-School-Id": schoolId },
      });
      const list = res.data;
      setSubcategories(Array.isArray(list) ? list : list?.data ?? []);
    } catch (err) {
      console.error("Error fetching subcategories:", err);
      setSubcategories([]);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ================= HANDLE INPUTS =================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setForm({ ...form, category_id: categoryId });
    fetchSubcategories(categoryId);
  };

  // ================= ADD / UPDATE SUBCATEGORY =================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);

    if (!form.category_id || !form.subcategory_name.trim()) {
      return setMsg({ type: "error", text: "Both category and subcategory name are required." });
    }

    const payload = { ...form, school_id: schoolId, created_by };

    try {
      let res;
      if (editId) {
        res = await api.post(`${baseURL}/update/${editId}`, payload, {
          headers: { "X-School-Id": schoolId },
        });
      } else {
        res = await api.post(`${baseURL}/add`, payload, {
          headers: { "X-School-Id": schoolId },
        });
      }

      const json = res.data;
      if (json?.status || json?.success) {
        setMsg({
          type: "success",
          text: json.message || (editId ? "Subcategory updated successfully" : "Subcategory added successfully"),
        });
        setForm({ category_id: "", subcategory_name: "" });
        setEditId(null);
        fetchSubcategories(payload.category_id);
      } else {
        setMsg({ type: "error", text: json?.message || "Operation failed." });
      }
    } catch (err) {
      console.error("Error saving subcategory:", err);
      setMsg({ type: "error", text: "Network or server error." });
    }
  };

  // ================= EDIT =================
  const handleEdit = (item) => {
    setForm({
      category_id: item.category_id,
      subcategory_name: item.subcategory_name,
    });
    setEditId(item.id);
    fetchSubcategories(item.category_id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subcategory?")) return;

    try {
      const res = await api.post(`${baseURL}/delete/${id}`, {}, {
        headers: { "X-School-Id": schoolId },
      });

      const json = res.data;
      if (json?.status || json?.success) {
        setMsg({ type: "success", text: json.message || "Subcategory deleted successfully." });
        fetchSubcategories(form.category_id);
      } else {
        setMsg({ type: "error", text: json?.message || "Delete failed." });
      }
    } catch (err) {
      console.error("Delete error:", err);
      setMsg({ type: "error", text: "Network or server error." });
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div style={{ flex: 1, backgroundColor: "#f4f6f8" }}>
        <main
          style={{
            padding: "30px",
            maxWidth: "1000px",
            margin: "auto",
            marginTop: "0px",
          }}
        >
          {/* Add/Edit Subcategory Section */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>
              {editId ? "✏️ Edit Subcategory" : "➕ Add New Subcategory"}
              {msg && <p style={{ color: msg.type === "error" ? "#b91c1c" : "#166534" }}>{msg.text}</p>}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={formGrid}>
                <FormGroup label="Select Category">
                  <select
                    name="category_id"
                    value={form.category_id}
                    onChange={handleCategoryChange}
                    style={fullWidthInput}
                    required
                  >
                    <option value="">-- Select --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.category_name}
                      </option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup label="Subcategory Name">
                  <input
                    type="text"
                    name="subcategory_name"
                    value={form.subcategory_name}
                    onChange={handleChange}
                    style={fullWidthInput}
                    required
                  />
                </FormGroup>
              </div>

              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <button type="submit" style={buttonStyle}>
                  {editId ? "Update Subcategory" : "Add Subcategory"}
                </button>
              </div>
            </form>
          </section>

          {/* View Subcategories Section */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>📋 All Subcategories</h2>
            {subcategories.length === 0 ? (
              <p>No subcategories found. Please Select Categories</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {subcategories.map((item, idx) => (
                  <li
                    key={item.id}
                    style={{
                      marginBottom: "15px",
                      padding: "15px",
                      borderRadius: "8px",
                      background: idx % 2 === 0 ? "#f9f9f9" : "#eef3f9",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: "16px", color: "#333" }}>
                        {item.subcategory_name}
                      </strong>
                      <div style={{ color: "#777", fontSize: "13px" }}>
                        Category:{" "}
                        {
                          categories.find((c) => c.id === item.category_id)
                            ?.category_name
                        }
                      </div>
                    </div>
                    <div>
                      <FaEdit
                        onClick={() => handleEdit(item)}
                        style={{
                          color: "#0077cc",
                          cursor: "pointer",
                          marginRight: "15px",
                        }}
                      />
                      <FaTrash
                        onClick={() => handleDelete(item.id)}
                        style={{ color: "red", cursor: "pointer" }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

// Reusable Form Group
const formGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "20px",
};

const fullWidthInput = {
  width: "100%",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "15px",
  boxSizing: "border-box",
};

const FormGroup = ({ label, children, style }) => (
  <div style={{ ...style }}>
    <label
      style={{
        marginBottom: "6px",
        display: "block",
        fontWeight: 500,
        color: "#444",
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const buttonStyle = {
  padding: "10px 25px",
  backgroundColor: "#0077cc",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "15px",
  cursor: "pointer",
};

const cardStyle = {
  background: "#ffffff",
  padding: "25px",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  marginBottom: "30px",
};

const titleStyle = {
  color: "#0077cc",
  marginBottom: "25px",
};

export default ProductSubcategory;
